// stores/useEnergyAnalysisStore.ts
import { create } from "zustand";

// ============================================================================
// 1. TYPE DEFINITIONS
// ============================================================================

/**
 * Hourly load/generation data (24 hours)
 * Index 0 = 00:00, Index 23 = 23:00
 */
type HourlyData = number[];

/**
 * Consumer Load Profile
 * Key: Consumer ID (e.g., "X1750417")
 * Value: Array of 24 hourly values
 */
type ConsumerLoadProfile = Record<string, HourlyData>;

/**
 * Plant Generation Profile
 * Key: Plant ID (e.g., "WIND25A001_NOV")
 * Value: Array of 24 hourly values
 */
type PlantGenerationProfile = Record<string, HourlyData>;

/**
 * Main Store State
 */
type EnergyAnalysisState = {
    // --- Data ---
    consumerLoadProfile: ConsumerLoadProfile;
    plantGenerationProfile: PlantGenerationProfile;

    // --- Loading States ---
    isLoadingConsumers: boolean;
    isLoadingPlants: boolean;
    isLoading: boolean; // Combined loading state

    // --- Error States (Optional) ---
    consumerError: string | null;
    plantError: string | null;

    // --- Actions: Setters ---
    setConsumerLoadProfile: (data: ConsumerLoadProfile) => void;
    setPlantGenerationProfile: (data: PlantGenerationProfile) => void;

    // --- Actions: Individual Consumer/Plant ---
    addConsumer: (consumerId: string, data: HourlyData) => void;
    removeConsumer: (consumerId: string) => void;
    addPlant: (plantId: string, data: HourlyData) => void;
    removePlant: (plantId: string) => void;

    // --- Actions: Loading States ---
    setLoadingConsumers: (loading: boolean) => void;
    setLoadingPlants: (loading: boolean) => void;

    // --- Actions: Error Handling ---
    setConsumerError: (error: string | null) => void;
    setPlantError: (error: string | null) => void;

    // --- Actions: Utilities ---
    clearAllData: () => void;
    clearConsumers: () => void;
    clearPlants: () => void;
    reset: () => void; // Reset to initial state
};

// ============================================================================
// 2. INITIAL STATE
// ============================================================================

const INITIAL_STATE = {
    consumerLoadProfile: {},
    plantGenerationProfile: {},
    isLoadingConsumers: false,
    isLoadingPlants: false,
    isLoading: false,
    consumerError: null,
    plantError: null,
};

// ============================================================================
// 3. STORE CREATION
// ============================================================================

export const useEnergyAnalysisStore = create<EnergyAnalysisState>((set: any, get: any) => ({
    // --- Initial State ---
    ...INITIAL_STATE,

    // --- Setters: Bulk Data ---
    setConsumerLoadProfile: (data: any) =>
        set({
            consumerLoadProfile: data,
            consumerError: null
        }),

    setPlantGenerationProfile: (data: any) =>
        set({
            plantGenerationProfile: data,
            plantError: null
        }),

    // --- Individual Consumer/Plant Management ---
    addConsumer: (consumerId: any, data: any) =>
        set((state: any) => ({
            consumerLoadProfile: {
                ...state.consumerLoadProfile,
                [consumerId]: data,
            },
        })),

    removeConsumer: (consumerId: any) =>
        set((state: any) => {
            const { [consumerId]: removed, ...rest } = state.consumerLoadProfile;
            return { consumerLoadProfile: rest };
        }),

    addPlant: (plantId: any, data: any) =>
        set((state: any) => ({
            plantGenerationProfile: {
                ...state.plantGenerationProfile,
                [plantId]: data,
            },
        })),

    removePlant: (plantId: any) =>
        set((state: any) => {
            const { [plantId]: removed, ...rest } = state.plantGenerationProfile;
            return { plantGenerationProfile: rest };
        }),

    // --- Loading State Management ---
    setLoadingConsumers: (loading: any) =>
        set((state: any) => ({
            isLoadingConsumers: loading,
            isLoading: loading || state.isLoadingPlants,
        })),

    setLoadingPlants: (loading: any) =>
        set((state: any) => ({
            isLoadingPlants: loading,
            isLoading: loading || state.isLoadingConsumers,
        })),

    // --- Error Handling ---
    setConsumerError: (error: any) =>
        set({ consumerError: error }),

    setPlantError: (error: any) =>
        set({ plantError: error }),

    // --- Utilities ---
    clearAllData: () =>
        set({
            consumerLoadProfile: {},
            plantGenerationProfile: {},
            consumerError: null,
            plantError: null,
        }),

    clearConsumers: () =>
        set({
            consumerLoadProfile: {},
            consumerError: null,
        }),

    clearPlants: () =>
        set({
            plantGenerationProfile: {},
            plantError: null,
        }),

    reset: () => set(INITIAL_STATE),
}));

// ============================================================================
// 4. UTILITY SELECTORS (Optional - for derived state)
// ============================================================================

/**
 * Get total number of consumers
 */
export const useConsumerCount = () =>
    useEnergyAnalysisStore((state: any) =>
        Object.keys(state.consumerLoadProfile).length
    );

/**
 * Get total number of plants
 */
export const usePlantCount = () =>
    useEnergyAnalysisStore((state: any) =>
        Object.keys(state.plantGenerationProfile).length
    );

/**
 * Get total consumer load for a specific hour (0-23)
 */
export const useTotalConsumerLoad = (hour: number) =>
    useEnergyAnalysisStore((state: any) => {
        return Object.values(state.consumerLoadProfile).reduce(
            (sum, hourlyData: any) => sum + (hourlyData[hour] || 0),
            0
        );
    });

/**
 * Get total plant generation for a specific hour (0-23)
 */
export const useTotalPlantGeneration = (hour: number) =>
    useEnergyAnalysisStore((state: any) => {
        return Object.values(state.plantGenerationProfile).reduce(
            (sum, hourlyData: any) => sum + (hourlyData[hour] || 0),
            0
        );
    });

/**
 * Get list of consumer IDs
 */
export const useConsumerIds = () =>
    useEnergyAnalysisStore((state: any) =>
        Object.keys(state.consumerLoadProfile)
    );

/**
 * Get list of plant IDs
 */
export const usePlantIds = () =>
    useEnergyAnalysisStore((state: any) =>
        Object.keys(state.plantGenerationProfile)
    );

/**
 * Check if any data exists
 */
export const useHasData = () =>
    useEnergyAnalysisStore((state: any) =>
        Object.keys(state.consumerLoadProfile).length > 0 ||
        Object.keys(state.plantGenerationProfile).length > 0
    );

/**
 * Get combined hourly data (total consumption + total generation)
 */
export const useCombinedHourlyData = () =>
    useEnergyAnalysisStore((state: any) => {
        const hours = Array.from({ length: 24 }, (_, i) => i);

        return hours.map(hour => {
            const totalLoad: any = Object.values(state.consumerLoadProfile).reduce(
                (sum, data: any) => sum + (data[hour] || 0),
                0
            );

            const totalGeneration: any = Object.values(state.plantGenerationProfile).reduce(
                (sum, data: any) => sum + (data[hour] || 0),
                0
            );

            return {
                hour,
                totalLoad,
                totalGeneration,
                difference: totalGeneration - totalLoad,
            };
        });
    });